import {
  GET_USERS_BY_DISTRICT_PAGINATED,
  GET_LATEST_QUALIFICATIONS_BY_USER_IDS,
  GET_USERS_SUMMARY_LIGHT,
  GET_USERS_BY_INSTITUTE_AND_DISTRICTS_PAGINATED,
  GET_USERS_BY_INSTITUTE_PAGINATED,
  GET_USERS_BY_IDS,
} from "GraphQl/Queries/User";
import { GET_SCORM_QUIZ_ATTEMPT_WITH_FILTER } from "GraphQl/Queries/Courses";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { asdmCourseReportConfig, asdmPhaseDateRanges } from "api/Consts";
import { Form } from "skillstrainer-resource-library";
import Container from "components/Container/Container";
import ASDMStatsFormBuilder from "formBuilders/ASDMStatsFormBuilder";
import MultiSelectDropdown from "components/InputGroup/MultiselectDropdown";

import { useLazyQuery, useApolloClient } from "@apollo/client";
import _ from "lodash";
import DataTable from "react-data-table-component";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const careerOptions = [
  {
    career_group: "Career Group 1",
    text: "Tourism & Hospitality, Beauty & Wellness, Healthcare & Life Sciences, Wholesale & Retail Trade",
  },
  {
    career_group: "Career Group 2",
    text: "Construction, Textile & Apparel, Storage, Electronics & Hardware, Transportation, Electricity, Gas, Water-Supply & Air Conditioning, Security, Domestic Job Roles",
  },
  {
    career_group: "Career Group 3",
    text: "Agriculture, Tea Production, Food Products / Food Processing",
  },
  {
    career_group: "Career Group 4",
    text: "IT & ITeS, Media & Entertainment",
  },
];

const SCORE_WORD_ASSOCIATION = {
  0: "Unlikely",
  1: "Unlikely",
  2: "Unlikely",
  3: "Likely",
  4: "Likely",
  5: "Preferred",
  6: "Preferred",
  7: "Most Preferred",
  8: "Most Preferred",
};

const OTHER_GENDER_LABEL = "Others";
const NO_CLASS_LABEL = "No Class Data";
const NO_INSTITUTE_LABEL = "NO INSTITUTE";
const NO_DISTRICT_LABEL = "NO DISTRICT";

const INTERPRETATION_LABELS = [
  "Unlikely",
  "Likely",
  "Preferred",
  "Most Preferred",
];

const CAREER_GROUPS = [
  { key: "g1", label: "Career Group 1", wordKey: "g1_word" },
  { key: "g2", label: "Career Group 2", wordKey: "g2_word" },
  { key: "g3", label: "Career Group 3", wordKey: "g3_word" },
  { key: "g4", label: "Career Group 4", wordKey: "g4_word" },
];

const GENDER_OPTIONS = [
  { value: "Male", label: "Male" },
  { value: "Female", label: "Female" },
  { value: OTHER_GENDER_LABEL, label: OTHER_GENDER_LABEL },
];

const createCareerGroupTemplate = () =>
  CAREER_GROUPS.map((group) => ({
    key: group.key,
    label: group.label,
    counts: INTERPRETATION_LABELS.reduce((acc, label) => {
      acc[label] = 0;
      return acc;
    }, {}),
  }));

const chunkArray = (items, chunkSize) => {
  const result = [];
  for (let i = 0; i < items.length; i += chunkSize)
    result.push(items.slice(i, i + chunkSize));
  return result;
};

const calculateScormScores = (attempts = []) => {
  if (!attempts.length) return {};

  const sorted = [...attempts].sort(
    (a, b) => new Date(b.created_at) - new Date(a.created_at),
  );

  const latestAttemptId = sorted[0].attempt_id;
  const latestAttemptResponses = sorted.filter(
    (r) => r.attempt_id === latestAttemptId,
  );

  const normalizeBySlide = (responses) => {
    return Object.values(
      _.groupBy(responses, (r) => r.slide_id || r.question_id),
    ).map((group) => _.maxBy(group, (g) => new Date(g.created_at).getTime()));
  };

  const responses = normalizeBySlide(latestAttemptResponses);

  let digitalScore = 0;
  let g1 = 0,
    g2 = 0,
    g3 = 0,
    g4 = 0;

  responses.forEach((res) => {
    const trimmed = `${res.user_response || ""}`.trim();
    if (!trimmed) return;

    const slideSource = res.slide_id || res.question_id || "";
    const slideMatch = /Slide(\d+)/i.exec(slideSource);
    const slideNum = slideMatch ? Number(slideMatch[1]) : null;

    const firstChar = trimmed[0];
    const lastChar = trimmed[trimmed.length - 1];

    if (slideNum !== null && slideNum >= 9 && slideNum <= 16) {
      if (firstChar === "A") digitalScore += 1;
      return;
    }

    if (firstChar === "D" || lastChar === "4") g4 += 1;
    if (firstChar === "C" || lastChar === "3") g3 += 1;
    if (firstChar === "B" || lastChar === "2") g2 += 1;
    if (firstChar === "A" || lastChar === "1") g1 += 1;
  });

  g1 = Math.min(g1, 8);
  g2 = Math.min(g2, 8);
  g3 = Math.min(g3, 8);
  g4 = Math.min(g4, 8);
  digitalScore = Math.min(digitalScore, 8);

  // 7️⃣ Final output
  return {
    g1m: `${g1}/8`,
    g2m: `${g2}/8`,
    g3m: `${g3}/8`,
    g4m: `${g4}/8`,
    g1_word: SCORE_WORD_ASSOCIATION[String(g1)] || "N/A",
    g2_word: SCORE_WORD_ASSOCIATION[String(g2)] || "N/A",
    g3_word: SCORE_WORD_ASSOCIATION[String(g3)] || "N/A",
    g4_word: SCORE_WORD_ASSOCIATION[String(g4)] || "N/A",
    digital_marks: `${digitalScore}/8`,
  };
};

const normalizeGender = (gender) => {
  const value = `${gender || ""}`.trim().toLowerCase();
  if (value === "0" || value === "male") return "Male";
  if (value === "1" || value === "female") return "Female";
  return OTHER_GENDER_LABEL;
};

const normalizeClass = (value) => {
  if (value === null || value === undefined) return NO_CLASS_LABEL;
  const normalized = String(value).trim();
  return normalized ? normalized : NO_CLASS_LABEL;
};

const getInstituteLabel = (enrollment) =>
  _.get(enrollment, "batch.institute.name") || NO_INSTITUTE_LABEL;

const getDistrictLabel = (enrollment) =>
  _.get(enrollment, "batch.institute.district") || NO_DISTRICT_LABEL;

const matchesMultiSelect = (value, selected = []) => {
  if (!selected.length) return true;
  return selected.includes(value);
};

const filterSummaryRows = (
  rows,
  {
    selectedDistricts = [],
    selectedInstitutes = [],
    selectedClasses = [],
    selectedGenders = [],
    ignore = {},
  },
) => {
  const districtSet =
    !ignore.district && selectedDistricts.length
      ? new Set(selectedDistricts)
      : null;
  const instituteSet =
    !ignore.institute && selectedInstitutes.length
      ? new Set(selectedInstitutes)
      : null;
  const classSet =
    !ignore.class && selectedClasses.length ? new Set(selectedClasses) : null;
  const genderSet =
    !ignore.gender && selectedGenders.length ? new Set(selectedGenders) : null;

  if (!districtSet && !instituteSet && !classSet && !genderSet) {
    return rows;
  }

  return rows.filter((row) => {
    if (districtSet && !districtSet.has(row.district)) return false;
    if (instituteSet && !instituteSet.has(row.institute)) return false;
    if (classSet && !classSet.has(row.class_label)) return false;
    if (genderSet && !genderSet.has(row.gender_label)) return false;
    return true;
  });
};

export default function AsdmViewOnly() {
  const client = useApolloClient();

  const [loadingPercent, setLoadingPercent] = useState(0);
  const [scormLoading, setScormLoading] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [formInitValues, setFormInitValues] = useState();

  const [summaryRows, setSummaryRows] = useState([]);

  const [expandedGroup, setExpandedGroup] = useState(null);
  const [selectedDistricts, setSelectedDistricts] = useState([]);
  const [selectedInstitutes, setSelectedInstitutes] = useState([]);
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [selectedGenders, setSelectedGenders] = useState([]);
  const [selectedCareerGroup, setSelectedCareerGroup] = useState("");
  const [selectedInterpretation, setSelectedInterpretation] = useState("");
  const [openPhasePanels, setOpenPhasePanels] = useState([]);
  const [careerGroupSummary, setCareerGroupSummary] = useState(() =>
    createCareerGroupTemplate(),
  );
  const [careerGroupLoading, setCareerGroupLoading] = useState(false);
  const [showCareerGroupInfo, setShowCareerGroupInfo] = useState(false);

  const [batchTable, setBatchTable] = useState({
    totalRows: 0,
    pageIndex: 0,
    pageSize: 25,
    rows: [],
  });

  const lastFilterRef = useRef(null);
  const lastAutoRangeRef = useRef("");
  const lastProjectIdsRef = useRef("");
  const careerGroupCacheRef = useRef(new Map());
  const careerGroupRequestIdRef = useRef(0);

  const phaseDateRanges = useMemo(() => {
    const formatDate = (date) => {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    };
    const tomorrowDate = new Date();
    tomorrowDate.setDate(tomorrowDate.getDate() + 1);
    const tomorrow = formatDate(tomorrowDate);
    return Object.entries(asdmPhaseDateRanges).reduce((acc, [phase, range]) => {
      acc[phase] = {
        start: range.start,
        end: range.end || tomorrow,
      };
      return acc;
    }, {});
  }, []);

  const projectIdToRange = useMemo(() => {
    const asdmProjectId = asdmCourseReportConfig.asdmProjectId ?? {};
    return Object.entries(asdmProjectId).reduce((acc, [phase, projectId]) => {
      const range = phaseDateRanges[phase];
      if (range) acc[projectId] = range;
      return acc;
    }, {});
  }, [phaseDateRanges]);

  const computePhaseRange = useCallback(
    (projectIds) => {
      const selected = Array.isArray(projectIds) ? projectIds : [projectIds];
      const ranges = selected.map((id) => projectIdToRange[id]).filter(Boolean);
      if (!ranges.length) return null;
      const parseDate = (iso) => new Date(`${iso}T00:00:00`);
      const formatDate = (date) => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, "0");
        const d = String(date.getDate()).padStart(2, "0");
        return `${y}-${m}-${d}`;
      };
      const minStart = ranges
        .map((r) => parseDate(r.start))
        .sort((a, b) => a - b)[0];
      const maxEnd = ranges
        .map((r) => parseDate(r.end))
        .sort((a, b) => b - a)[0];
      return {
        start: formatDate(minStart),
        end: formatDate(maxEnd),
      };
    },
    [projectIdToRange],
  );

  const [runQuerySummaryIds] = useLazyQuery(GET_USERS_SUMMARY_LIGHT, {
    fetchPolicy: "network-only",
  });

  const [runQueryUsersByIds] = useLazyQuery(GET_USERS_BY_IDS, {
    fetchPolicy: "network-only",
  });

  const [runQueryInstituteUsers] = useLazyQuery(
    GET_USERS_BY_INSTITUTE_PAGINATED,
    {
      fetchPolicy: "network-only",
    },
  );

  const [runQueryInstituteUsersByDistricts] = useLazyQuery(
    GET_USERS_BY_INSTITUTE_AND_DISTRICTS_PAGINATED,
    {
      fetchPolicy: "network-only",
    },
  );

  const [runQueryDistrictUsers] = useLazyQuery(
    GET_USERS_BY_DISTRICT_PAGINATED,
    {
      fetchPolicy: "network-only",
    },
  );

  const [runQueryScormAttempts] = useLazyQuery(
    GET_SCORM_QUIZ_ATTEMPT_WITH_FILTER,
    {
      fetchPolicy: "network-only",
    },
  );

  const [runQueryLatestQualifications] = useLazyQuery(
    GET_LATEST_QUALIFICATIONS_BY_USER_IDS,
    { fetchPolicy: "network-only" },
  );

  const scormMappingIds = useMemo(
    () => asdmCourseReportConfig.mappingId || [],
    [],
  );

  const allProjectIds = useMemo(
    () =>
      Object.values(asdmCourseReportConfig.asdmProjectId || {}).filter(Boolean),
    [],
  );

  const normalizeProjectIds = useCallback(
    (value) => {
      const list = Array.isArray(value) ? value : value ? [value] : [];
      if (list.length) return list;
      if (allProjectIds.length) return allProjectIds;
      if (asdmCourseReportConfig.projectId)
        return [asdmCourseReportConfig.projectId];
      return [];
    },
    [allProjectIds],
  );

  const buildScormAttemptFilter = useCallback(
    (userIds = []) => {
      const filter = { user_id: { _in: userIds } };
      if (scormMappingIds.length) {
        filter.mapping_id = { _in: scormMappingIds };
      }
      return filter;
    },
    [scormMappingIds],
  );

  const fetchScormAttemptsByUsers = useCallback(
    async (userIds = []) => {
      if (!userIds.length) return [];
      const chunks = chunkArray(userIds, 500);
      let scormAttempts = [];
      for (const c of chunks) {
        const { data } = await runQueryScormAttempts({
          variables: {
            scormQuizAttemptSearchExp: buildScormAttemptFilter(c),
          },
        });
        scormAttempts.push(
          ...(data?.courses_course_scorm_quiz_attempt_data || []),
        );
      }
      return scormAttempts;
    },
    [buildScormAttemptFilter, runQueryScormAttempts],
  );

  const userTableColumns = useMemo(
    () => [
      { name: "Name", selector: (row) => row.name || "N/A", wrap: true },
      {
        name: "Mobile Number",
        selector: (row) => row.mobile_number || "N/A",
        wrap: true,
      },
      { name: "Email", selector: (row) => row.email || "N/A", wrap: true },
      {
        name: "Registration Date",
        selector: (row) => row.registrationDate || "N/A",
        wrap: true,
      },
      {
        name: "Certification Date",
        selector: (row) => row.certificationDate || "N/A",
        wrap: true,
      },
      {
        name: "Certificate",
        cell: (row) =>
          row.certUrl ? (
            <a
              href={row.certUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 underline"
            >
              View PDF
            </a>
          ) : (
            "—"
          ),
        center: true,
      },
      {
        name: (
          <div style={{ wordBreak: "break-word" }}>Career Group 1 Marks</div>
        ),
        selector: (row) => row.g1m || "N/A",
        width: "160px",
      },

      {
        name: (
          <div style={{ wordBreak: "break-word" }}>
            Career Group 1 Interpretation
          </div>
        ),
        selector: (row) => row.g1_word || "N/A",
        width: "160px",
      },
      {
        name: (
          <div style={{ wordBreak: "break-word" }}>Career Group 2 Marks</div>
        ),
        selector: (row) => row.g2m || "N/A",
        width: "160px",
      },
      {
        name: (
          <div style={{ wordBreak: "break-word" }}>
            Career Group 2 Interpretation
          </div>
        ),
        selector: (row) => row.g2_word || "N/A",
        width: "160px",
      },
      {
        name: (
          <div style={{ wordBreak: "break-word" }}>Career Group 3 Marks</div>
        ),
        selector: (row) => row.g3m || "N/A",
        width: "160px",
      },
      {
        name: (
          <div style={{ wordBreak: "break-word" }}>
            Career Group 3 Interpretation
          </div>
        ),
        selector: (row) => row.g3_word || "N/A",
        width: "160px",
      },
      {
        name: (
          <div style={{ wordBreak: "break-word" }}>Career Group 4 Marks</div>
        ),
        selector: (row) => row.g4m || "N/A",
        width: "160px",
      },
      {
        name: (
          <div style={{ wordBreak: "break-word" }}>
            Career Group 4 Interpretation
          </div>
        ),
        selector: (row) => row.g4_word || "N/A",
        width: "160px",
      },
      {
        name: (
          <div style={{ wordBreak: "break-word" }}>Digital Literacy Score</div>
        ),
        selector: (row) => row.digital_marks || "N/A",
        width: "160px",
      },
    ],
    [],
  );

  const userTableStyles = useMemo(
    () => ({
      headCells: { style: { fontWeight: 600, fontSize: "13px" } },
      cells: {
        style: { fontSize: "13px", paddingTop: "8px", paddingBottom: "8px" },
      },
    }),
    [],
  );

  const fetchLatestQualifications = useCallback(
    async (userIds = []) => {
      if (!userIds.length) return {};
      const map = {};
      const chunks = chunkArray(userIds, 1000);
      for (const c of chunks) {
        const { data } = await runQueryLatestQualifications({
          variables: { user_ids: c },
        });
        (data?.courses_user_academic_qualifications || []).forEach((row) => {
          if (row?.qualifications_lookup?.name) {
            map[row.user_id] = row.qualifications_lookup.name;
          }
        });
      }
      return map;
    },
    [runQueryLatestQualifications],
  );

  const filterStats = async (filter) => {
    try {
      setLoadingPercent(0);
      setExpandedGroup(null);
      setBatchTable({ totalRows: 0, pageIndex: 0, pageSize: 25, rows: [] });
      setSelectedDistricts([]);
      setSelectedInstitutes([]);
      setSelectedClasses([]);
      setSelectedGenders([]);
      setSummaryRows([]);
      setCareerGroupSummary(createCareerGroupTemplate());
      setCareerGroupLoading(false);
      careerGroupCacheRef.current.clear();

      const startDate = filter?.start_date?.split("T")[0] || "2024-04-01";
      const endDate =
        filter?.end_date?.split("T")[0] ||
        new Date().toISOString().split("T")[0];

      const projectIds = normalizeProjectIds(filter.project_id);

      lastFilterRef.current = {
        startDate,
        endDate,
        project_ids: projectIds,
        course_ids: [178, 205],
      };

      const nextSummaryRows = [];

      const pageSize = 2000;
      let offset = 0;

      setLoadingPercent(5);

      while (true) {
        const { data } = await runQuerySummaryIds({
          variables: {
            startDate,
            endDate,
            course_ids: [178, 205],
            project_ids: projectIds,
            limit: pageSize,
            offset,
          },
        });

        const users = data?.courses_users || [];
        if (!users.length) break;

        const userIds = users.map((u) => u.id).filter(Boolean);
        const qualificationMap = await fetchLatestQualifications(userIds);

        users.forEach((u) => {
          const enrollment = _.get(u, "batch_enrollments.0");
          const inst = getInstituteLabel(enrollment);
          const dist = getDistrictLabel(enrollment);
          const classLabel = normalizeClass(qualificationMap[u.id]);
          const genderLabel = normalizeGender(u.gender);
          const projectId = _.get(enrollment, "batch.project_id");

          nextSummaryRows.push({
            user_id: u.id,
            institute: inst,
            district: dist,
            class_label: classLabel,
            gender_label: genderLabel,
            project_id: projectId,
          });
        });

        offset += pageSize;

        setLoadingPercent((p) => Math.min(45, p + 5));

        await new Promise((r) => setTimeout(r, 0));
      }

      setSummaryRows(nextSummaryRows);

      setLoadingPercent(100);

      try {
        client.cache.evict({ fieldName: "courses_users" });
        client.cache.gc();
      } catch (_) {}
    } catch (e) {
      console.error("Error while fetching summary:", e);
      setLoadingPercent(0);
    }
  };

  const loadInstitutePage = useCallback(
    async ({ instituteName, pageIndex, pageSize }) => {
      const base = lastFilterRef.current;
      if (!base) return;

      setScormLoading(true);
      setLoadingPercent(10);

      const offset = pageIndex * pageSize;

      const hasDistrictFilter = selectedDistricts.length > 0;
      const { data } = await (
        hasDistrictFilter
          ? runQueryInstituteUsersByDistricts
          : runQueryInstituteUsers
      )({
        variables: {
          ...base,
          institute_name: instituteName,
          limit: pageSize,
          offset,
          ...(hasDistrictFilter ? { districts: selectedDistricts } : {}),
        },
      });

      const totalRows = data?.courses_users_aggregate?.aggregate?.count || 0;
      const users = data?.courses_users || [];
      setLoadingPercent(35);

      const userIds = users.map((u) => u.id).filter(Boolean);
      const qualificationMap = await fetchLatestQualifications(userIds);

      let usersWithScorm = users;

      if (userIds.length) {
        const scormAttempts = await fetchScormAttemptsByUsers(userIds);

        const attemptsByUserId = _.groupBy(scormAttempts, "user_id");

        usersWithScorm = users.map((u) => {
          const attempts = attemptsByUserId[u.id] || [];
          return { ...u, scorm_scores: calculateScormScores(attempts) };
        });
      }

      setLoadingPercent(85);

      const normalizedRows = usersWithScorm.map((user) => {
        const certUrl = _.get(
          user,
          "batch_enrollments.0.course_enrolment.course_complete_obj.certificate.certificate_pdf_url",
        );
        const registrationDate = _.get(user, "batch_enrollments.0.updated_at");
        const certificationDate = _.get(
          user,
          "batch_enrollments.0.course_enrolment.course_complete_obj.created_at",
        );

        const sc = user.scorm_scores || {};
        const genderLabel = normalizeGender(user.gender);
        const classLabel = normalizeClass(qualificationMap[user.id]);
        return {
          ...user,
          certUrl,
          registrationDate,
          certificationDate,
          g1m: sc.g1m,
          g1_word: sc.g1_word,
          g2m: sc.g2m,
          g2_word: sc.g2_word,
          g3m: sc.g3m,
          g3_word: sc.g3_word,
          g4m: sc.g4m,
          g4_word: sc.g4_word,
          digital_marks: sc.digital_marks,
          gender_label: genderLabel,
          class_label: classLabel,
        };
      });

      setBatchTable({
        totalRows,
        pageIndex,
        pageSize,
        rows: normalizedRows,
      });

      setLoadingPercent(100);
      setScormLoading(false);

      try {
        client.cache.evict({
          fieldName: "courses_course_scorm_quiz_attempt_data",
        });
        client.cache.evict({ fieldName: "courses_course_module_user_attempt" });
        client.cache.gc();
      } catch (_) {}
    },
    [
      runQueryInstituteUsers,
      runQueryInstituteUsersByDistricts,
      fetchScormAttemptsByUsers,
      client,
      fetchLatestQualifications,
      selectedDistricts,
    ],
  );

  const loadDistrictPage = useCallback(
    async ({ districtName, pageIndex, pageSize }) => {
      const base = lastFilterRef.current;
      if (!base) return;

      setScormLoading(true);
      setLoadingPercent(10);

      const offset = pageIndex * pageSize;

      const { data } = await runQueryDistrictUsers({
        variables: {
          ...base,
          district: districtName,
          limit: pageSize,
          offset,
        },
      });

      const totalRows = data?.courses_users_aggregate?.aggregate?.count || 0;
      const users = data?.courses_users || [];
      setLoadingPercent(35);

      const userIds = users.map((u) => u.id).filter(Boolean);
      const qualificationMap = await fetchLatestQualifications(userIds);

      let usersWithScorm = users;

      if (userIds.length) {
        const scormAttempts = await fetchScormAttemptsByUsers(userIds);

        const attemptsByUserId = _.groupBy(scormAttempts, "user_id");

        usersWithScorm = users.map((u) => {
          console.log("uuu", u);
          console.log("attemptsByUserId", attemptsByUserId);

          const attempts = attemptsByUserId[u.id] || [];
          return { ...u, scorm_scores: calculateScormScores(attempts) };
        });
      }

      setLoadingPercent(85);

      const normalizedRows = usersWithScorm.map((user) => {
        const certUrl = _.get(
          user,
          "batch_enrollments.0.course_enrolment.course_complete_obj.certificate.certificate_pdf_url",
        );
        const registrationDate = _.get(user, "batch_enrollments.0.updated_at");
        const certificationDate = _.get(
          user,
          "batch_enrollments.0.course_enrolment.course_complete_obj.created_at",
        );

        const sc = user.scorm_scores || {};
        const genderLabel = normalizeGender(user.gender);
        const classLabel = normalizeClass(qualificationMap[user.id]);
        return {
          ...user,
          certUrl,
          registrationDate,
          certificationDate,
          g1m: sc.g1m,
          g1_word: sc.g1_word,
          g2m: sc.g2m,
          g2_word: sc.g2_word,
          g3m: sc.g3m,
          g3_word: sc.g3_word,
          g4m: sc.g4m,
          g4_word: sc.g4_word,
          digital_marks: sc.digital_marks,
          gender_label: genderLabel,
          class_label: classLabel,
        };
      });

      setBatchTable({
        totalRows,
        pageIndex,
        pageSize,
        rows: normalizedRows,
      });

      setLoadingPercent(100);
      setScormLoading(false);

      try {
        client.cache.evict({
          fieldName: "courses_course_scorm_quiz_attempt_data",
        });
        client.cache.evict({ fieldName: "courses_course_module_user_attempt" });
        client.cache.gc();
      } catch (_) {}
    },
    [
      runQueryDistrictUsers,
      fetchScormAttemptsByUsers,
      client,
      fetchLatestQualifications,
    ],
  );

  const groupMode =
    selectedInstitutes.length > 0
      ? "institute"
      : selectedDistricts.length > 0
        ? "district"
        : "institute";

  const onToggleGroup = async (groupName) => {
    const next = expandedGroup === groupName ? null : groupName;
    setExpandedGroup(next);

    if (!next) return;

    const pageSize = batchTable.pageSize || 25;
    if (groupMode === "district") {
      await loadDistrictPage({
        districtName: next,
        pageIndex: 0,
        pageSize,
      });
      return;
    }

    await loadInstitutePage({
      instituteName: next,
      pageIndex: 0,
      pageSize,
    });
  };

  const downloadExcelForFilters = async () => {
    const base = lastFilterRef.current;
    if (!base) return;

    const pageSize = 500;
    if (!filteredSummaryRows.length) return;

    const exportUserIds = Array.from(
      new Set(filteredSummaryRows.map((row) => row.user_id).filter(Boolean)),
    );
    if (!exportUserIds.length) return;

    const summaryByUserId = new Map(
      filteredSummaryRows.map((row) => [row.user_id, row]),
    );

    const ws = XLSX.utils.aoa_to_sheet([
      [
        "S. No.",
        "Name",
        "Mobile Number",
        "Email",
        "Gender",
        "Qualification",
        "Phase",
        "Institute Name",
        "District",
        "Registration Date",
        "Certification Date",
        "Certificate URL",
        "Career Group 1 Marks",
        "Career Group 1 Interpretation",
        "Career Group 2 Marks",
        "Career Group 2 Interpretation",
        "Career Group 3 Marks",
        "Career Group 3 Interpretation",
        "Career Group 4 Marks",
        "Career Group 4 Interpretation",
        "Digital Literacy Score",
      ],
    ]);

    let serial = 1;
    setScormLoading(true);
    setLoadingPercent(5);

    const userChunks = chunkArray(exportUserIds, pageSize);
    let processed = 0;

    for (const chunk of userChunks) {
      const { data } = await runQueryUsersByIds({
        variables: { user_ids: chunk, project_ids: base.project_ids },
      });

      const users = data?.courses_users || [];
      if (!users.length) {
        processed += chunk.length;
        continue;
      }

      const qualificationMap = await fetchLatestQualifications(chunk);

      const scormAttempts = await fetchScormAttemptsByUsers(chunk);

      const attemptsByUserId = _.groupBy(scormAttempts, "user_id");

      const rows = users
        .map((u) => {
          const summary = summaryByUserId.get(u.id);
          if (!summary) return null;

          const enrollments = u.batch_enrollments || [];
          const matchedEnrollment =
            enrollments.find(
              (enrollment) =>
                getInstituteLabel(enrollment) === summary.institute &&
                getDistrictLabel(enrollment) === summary.district,
            ) || enrollments[0];

          const certUrl = _.get(
            matchedEnrollment,
            "course_enrolment.course_complete_obj.certificate.certificate_pdf_url",
          );
          const registrationDate = matchedEnrollment?.updated_at || "N/A";
          const certificationDate =
            matchedEnrollment?.course_enrolment?.course_complete_obj
              ?.created_at || "N/A";

          const scores = calculateScormScores(attemptsByUserId[u.id] || []);
          const genderLabel = summary.gender_label || normalizeGender(u.gender);
          const classLabel =
            summary.class_label || normalizeClass(qualificationMap[u.id]);
          const phaseLabel =
            projectIdToPhaseLabel[summary.project_id] || "Unknown";

          return [
            serial++,
            u.name || "N/A",
            u.mobile_number || "N/A",
            u.email || "N/A",
            genderLabel || "N/A",
            classLabel || NO_CLASS_LABEL,
            phaseLabel,
            summary.institute || NO_INSTITUTE_LABEL,
            summary.district || NO_DISTRICT_LABEL,
            registrationDate,
            certificationDate,
            certUrl || "—",
            scores.g1m || "N/A",
            scores.g1_word || "N/A",
            scores.g2m || "N/A",
            scores.g2_word || "N/A",
            scores.g3m || "N/A",
            scores.g3_word || "N/A",
            scores.g4m || "N/A",
            scores.g4_word || "N/A",
            scores.digital_marks || "N/A",
          ];
        })
        .filter(Boolean);

      XLSX.utils.sheet_add_aoa(ws, rows, { origin: -1 });

      processed += chunk.length;
      setLoadingPercent(
        5 + Math.round((processed / exportUserIds.length) * 90),
      );
      await new Promise((r) => setTimeout(r, 0));
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "ASDM Report");

    const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([buffer], { type: "application/octet-stream" }),
      `ASDM_Filtered_Report.xlsx`,
    );

    setLoadingPercent(100);
    setScormLoading(false);
  };

  const filteredSummaryRows = useMemo(
    () =>
      filterSummaryRows(summaryRows, {
        selectedDistricts,
        selectedInstitutes,
        selectedClasses,
        selectedGenders,
      }),
    [
      summaryRows,
      selectedDistricts,
      selectedInstitutes,
      selectedClasses,
      selectedGenders,
    ],
  );

  const instituteEntries = useMemo(() => {
    const totals = filteredSummaryRows.reduce((acc, row) => {
      acc[row.institute] = (acc[row.institute] || 0) + 1;
      return acc;
    }, {});
    return Object.entries(totals).sort((a, b) => (b[1] || 0) - (a[1] || 0));
  }, [filteredSummaryRows]);

  const districtEntries = useMemo(() => {
    const totals = filteredSummaryRows.reduce((acc, row) => {
      acc[row.district] = (acc[row.district] || 0) + 1;
      return acc;
    }, {});
    return Object.entries(totals).sort((a, b) => (b[1] || 0) - (a[1] || 0));
  }, [filteredSummaryRows]);

  const projectIdToPhaseLabel = useMemo(() => {
    const asdmProjectId = asdmCourseReportConfig.asdmProjectId ?? {};
    return Object.entries(asdmProjectId).reduce(
      (acc, [phaseKey, projectId]) => {
        acc[projectId] = phaseKey.replace("phase", "Phase ");
        return acc;
      },
      {},
    );
  }, []);

  const phaseDistrictEntries = useMemo(() => {
    if (!summaryRows.length) return [];
    const rows = filterSummaryRows(summaryRows, {
      selectedDistricts,
      selectedInstitutes,
      selectedClasses,
      selectedGenders,
    });
    const phaseMap = new Map();
    rows.forEach((row) => {
      const district = row.district || NO_DISTRICT_LABEL;
      const phaseLabel = projectIdToPhaseLabel[row.project_id] || "Unknown";
      if (!phaseMap.has(phaseLabel)) phaseMap.set(phaseLabel, new Set());
      phaseMap.get(phaseLabel).add(district);
    });

    const phaseOrder = Object.values(
      asdmCourseReportConfig.asdmProjectId || {},
    ).map((projectId) => projectIdToPhaseLabel[projectId]);

    return Array.from(phaseMap.entries())
      .map(([phase, districts]) => ({
        phase,
        districts: Array.from(districts).sort((a, b) => a.localeCompare(b)),
      }))
      .sort((a, b) => {
        const ai = phaseOrder.indexOf(a.phase);
        const bi = phaseOrder.indexOf(b.phase);
        if (ai === -1 && bi === -1) return a.phase.localeCompare(b.phase);
        if (ai === -1) return 1;
        if (bi === -1) return -1;
        return ai - bi;
      });
  }, [
    summaryRows,
    selectedDistricts,
    selectedInstitutes,
    selectedClasses,
    selectedGenders,
    projectIdToPhaseLabel,
  ]);

  const phaseInstituteEntries = useMemo(() => {
    if (!summaryRows.length) return [];
    const rows = filterSummaryRows(summaryRows, {
      selectedDistricts,
      selectedInstitutes,
      selectedClasses,
      selectedGenders,
    });
    const phaseMap = new Map();
    rows.forEach((row) => {
      const institute = row.institute || NO_INSTITUTE_LABEL;
      const phaseLabel = projectIdToPhaseLabel[row.project_id] || "Unknown";
      if (!phaseMap.has(phaseLabel)) phaseMap.set(phaseLabel, new Set());
      phaseMap.get(phaseLabel).add(institute);
    });

    const phaseOrder = Object.values(
      asdmCourseReportConfig.asdmProjectId || {},
    ).map((projectId) => projectIdToPhaseLabel[projectId]);

    return Array.from(phaseMap.entries())
      .map(([phase, institutes]) => ({
        phase,
        institutes: Array.from(institutes).sort((a, b) => a.localeCompare(b)),
      }))
      .sort((a, b) => {
        const ai = phaseOrder.indexOf(a.phase);
        const bi = phaseOrder.indexOf(b.phase);
        if (ai === -1 && bi === -1) return a.phase.localeCompare(b.phase);
        if (ai === -1) return 1;
        if (bi === -1) return -1;
        return ai - bi;
      });
  }, [
    summaryRows,
    selectedDistricts,
    selectedInstitutes,
    selectedClasses,
    selectedGenders,
    projectIdToPhaseLabel,
  ]);

  const groupEntries = useMemo(
    () => (groupMode === "district" ? districtEntries : instituteEntries),
    [groupMode, districtEntries, instituteEntries],
  );

  const groupHeading = useMemo(
    () =>
      groupMode === "district" ? "Users by District" : "Users by Institute",
    [groupMode],
  );

  const districtOptions = useMemo(() => {
    const rows = filterSummaryRows(summaryRows, {
      selectedDistricts,
      selectedInstitutes,
      selectedClasses,
      selectedGenders,
      ignore: { district: true },
    });
    const districts = Array.from(new Set(rows.map((row) => row.district)));
    return districts
      .sort((a, b) => a.localeCompare(b))
      .map((district) => ({ value: district, label: district }));
  }, [summaryRows, selectedInstitutes, selectedClasses, selectedGenders]);

  const instituteOptions = useMemo(() => {
    const rows = filterSummaryRows(summaryRows, {
      selectedDistricts,
      selectedInstitutes,
      selectedClasses,
      selectedGenders,
      ignore: { institute: true },
    });
    const institutes = Array.from(new Set(rows.map((row) => row.institute)));
    return institutes
      .sort((a, b) => a.localeCompare(b))
      .map((inst) => ({ value: inst, label: inst }));
  }, [summaryRows, selectedDistricts, selectedClasses, selectedGenders]);

  const classOptions = useMemo(() => {
    const rows = filterSummaryRows(summaryRows, {
      selectedDistricts,
      selectedInstitutes,
      selectedClasses,
      selectedGenders,
      ignore: { class: true },
    });
    const classes = Array.from(new Set(rows.map((row) => row.class_label)));
    return classes
      .sort((a, b) => a.localeCompare(b))
      .map((classLabel) => ({ value: classLabel, label: classLabel }));
  }, [summaryRows, selectedDistricts, selectedInstitutes, selectedGenders]);

  const hasRowFilters = selectedClasses.length || selectedGenders.length;

  const filteredBatchRows = useMemo(
    () =>
      hasRowFilters
        ? batchTable.rows.filter((row) => {
            const matchesGender = matchesMultiSelect(
              row.gender_label,
              selectedGenders,
            );
            if (!matchesGender) return false;
            const matchesClass = matchesMultiSelect(
              row.class_label,
              selectedClasses,
            );
            if (!matchesClass) return false;
            return true;
          })
        : batchTable.rows,
    [batchTable.rows, hasRowFilters, selectedClasses, selectedGenders],
  );

  const hasCareerGroupUsers = useMemo(() => {
    if (!expandedGroup) return false;
    return filteredSummaryRows.some((row) =>
      groupMode === "district"
        ? row.district === expandedGroup
        : row.institute === expandedGroup,
    );
  }, [expandedGroup, filteredSummaryRows, groupMode]);

  const buildCareerGroupCounts = useCallback(
    async (userIds = []) => {
      const template = createCareerGroupTemplate();
      if (!userIds.length) return template;

      const summaryMap = new Map(template.map((entry) => [entry.key, entry]));
      const chunks = chunkArray(userIds, 500);

      for (const userChunk of chunks) {
        const scormAttempts = await fetchScormAttemptsByUsers(userChunk);

        const attemptsByUserId = _.groupBy(scormAttempts, "user_id");

        userChunk.forEach((userId) => {
          const scores = calculateScormScores(attemptsByUserId[userId] || []);
          CAREER_GROUPS.forEach((group) => {
            const interpretation = scores[group.wordKey];
            if (!INTERPRETATION_LABELS.includes(interpretation)) return;
            summaryMap.get(group.key).counts[interpretation] += 1;
          });
        });

        await new Promise((r) => setTimeout(r, 0));
      }

      return template;
    },
    [fetchScormAttemptsByUsers],
  );

  const careerGroupKey = useMemo(() => {
    if (!expandedGroup) return "";
    return JSON.stringify({
      groupMode,
      groupName: expandedGroup,
      districts: [...selectedDistricts].sort(),
      institutes: [...selectedInstitutes].sort(),
      classes: [...selectedClasses].sort(),
      genders: [...selectedGenders].sort(),
    });
  }, [
    expandedGroup,
    groupMode,
    selectedDistricts,
    selectedInstitutes,
    selectedClasses,
    selectedGenders,
  ]);

  useEffect(() => {
    if (!expandedGroup) {
      setCareerGroupSummary(createCareerGroupTemplate());
      setCareerGroupLoading(false);
      return;
    }

    const base = lastFilterRef.current;
    if (!base) return;

    const groupRows = filteredSummaryRows.filter((row) =>
      groupMode === "district"
        ? row.district === expandedGroup
        : row.institute === expandedGroup,
    );

    const userIds = Array.from(
      new Set(groupRows.map((row) => row.user_id).filter(Boolean)),
    );

    if (!userIds.length) {
      setCareerGroupSummary(createCareerGroupTemplate());
      setCareerGroupLoading(false);
      return;
    }

    if (careerGroupCacheRef.current.has(careerGroupKey)) {
      setCareerGroupSummary(careerGroupCacheRef.current.get(careerGroupKey));
      setCareerGroupLoading(false);
      return;
    }

    const requestId = careerGroupRequestIdRef.current + 1;
    careerGroupRequestIdRef.current = requestId;
    setCareerGroupLoading(true);
    setCareerGroupSummary(createCareerGroupTemplate());

    buildCareerGroupCounts(userIds)
      .then((summary) => {
        if (careerGroupRequestIdRef.current !== requestId) return;
        careerGroupCacheRef.current.set(careerGroupKey, summary);
        setCareerGroupSummary(summary);
        setCareerGroupLoading(false);
      })
      .catch(() => {
        if (careerGroupRequestIdRef.current !== requestId) return;
        setCareerGroupLoading(false);
      });
  }, [
    buildCareerGroupCounts,
    careerGroupKey,
    expandedGroup,
    filteredSummaryRows,
    groupMode,
  ]);

  const selectedInsightCount = useMemo(() => {
    if (!selectedCareerGroup || !selectedInterpretation) return null;
    const entry = careerGroupSummary.find(
      (item) => item.key === selectedCareerGroup,
    );
    if (!entry) return null;
    return entry.counts[selectedInterpretation] ?? 0;
  }, [careerGroupSummary, selectedCareerGroup, selectedInterpretation]);

  const selectedCareerGroupLabel = useMemo(() => {
    const entry = CAREER_GROUPS.find(
      (group) => group.key === selectedCareerGroup,
    );
    return entry?.label || "";
  }, [selectedCareerGroup]);

  const careerGroupOptions = useMemo(
    () =>
      CAREER_GROUPS.map((group) => ({ value: group.key, label: group.label })),
    [],
  );

  const interpretationOptions = useMemo(
    () => INTERPRETATION_LABELS.map((label) => ({ value: label, label })),
    [],
  );

  const hasActiveFilters = Boolean(
    selectedDistricts.length ||
    selectedInstitutes.length ||
    selectedClasses.length ||
    selectedGenders.length,
  );

  useEffect(() => {
    if (!selectedInstitutes.length) return;
    const available = new Set(instituteOptions.map((opt) => opt.value));
    const nextSelected = selectedInstitutes.filter((inst) =>
      available.has(inst),
    );
    if (nextSelected.length !== selectedInstitutes.length) {
      setSelectedInstitutes(nextSelected);
    }
  }, [instituteOptions, selectedInstitutes]);

  useEffect(() => {
    if (!expandedGroup) return;
    const isVisible = groupEntries.some(
      ([groupName]) => groupName === expandedGroup,
    );
    if (!isVisible) {
      setExpandedGroup(null);
      setBatchTable((prev) => ({
        ...prev,
        totalRows: 0,
        pageIndex: 0,
        rows: [],
      }));
    }
  }, [expandedGroup, groupEntries]);

  return (
    <Container>
      <div className="mx-auto max-w-[1400px] px-2 pb-20">
        {/* Header */}
        <div className="sticky top-4 z-40 mb-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-xl font-semibold tracking-tight text-slate-900">
                ASDM Data Explorer
              </h1>
              <p className="mt-1 text-sm text-slate-600">
                Select phase, then click{" "}
                <span className="font-medium text-slate-800">Filter</span> to
                fetch data.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {!!summaryRows.length && (
                <button
                  type="button"
                  onClick={() => setShowCareerGroupInfo(true)}
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-800 shadow-sm transition hover:bg-slate-50"
                >
                  ℹ️ Career group meanings
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Filter Form */}
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1fr_auto] xl:items-end">
            <Form
              key={formKey}
              formBuilder={ASDMStatsFormBuilder}
              className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4"
              submitButton={{
                text:
                  loadingPercent > 0 && loadingPercent < 100
                    ? `Filter`
                    : "Filter",
                className:
                  "inline-flex justify-center rounded-lg bg-orange px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:opacity-95",
              }}
              initValues={formInitValues}
              onUpdate={({ values }) => {
                const selectedProjectIds = Array.isArray(values?.project_id)
                  ? values.project_id
                  : values?.project_id
                    ? [values.project_id]
                    : [];
                if (!selectedProjectIds.length) return;

                const projectSignature = selectedProjectIds
                  .slice()
                  .sort()
                  .join(",");
                if (projectSignature === lastProjectIdsRef.current) return;
                lastProjectIdsRef.current = projectSignature;

                const range = computePhaseRange(selectedProjectIds);
                if (!range) return;

                const currentStart = values?.start_date
                  ? values.start_date.split("T")[0]
                  : "";
                const currentEnd = values?.end_date
                  ? values.end_date.split("T")[0]
                  : "";

                if (currentStart === range.start && currentEnd === range.end)
                  return;

                const signature = `${selectedProjectIds
                  .slice()
                  .sort()
                  .join(",")}|${range.start}|${range.end}`;
                if (lastAutoRangeRef.current === signature) return;
                lastAutoRangeRef.current = signature;

                setFormInitValues({
                  ...values,
                  start_date: range.start,
                  end_date: range.end,
                  project_id: selectedProjectIds,
                });
                setFormKey((k) => k + 1);
              }}
              onSubmit={(filter) => filterStats(filter)}
            />

            {loadingPercent > 0 && loadingPercent < 100 && (
              <div className="pl-4 text-sm font-semibold text-slate-800">
                Loading… {loadingPercent}%
              </div>
            )}
          </div>
        </div>

        {/* Refine Filters */}
        {!!summaryRows.length && (
          <div className="mt-6 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-slate-900">
                Refine results
              </h3>
              <button
                type="button"
                onClick={() => {
                  setSelectedDistricts([]);
                  setSelectedInstitutes([]);
                  setSelectedClasses([]);
                  setSelectedGenders([]);
                  setSelectedCareerGroup("");
                  setSelectedInterpretation("");
                  setExpandedGroup(null);
                }}
                disabled={!hasActiveFilters}
                className="text-sm font-semibold text-blue-600 hover:underline disabled:opacity-50"
              >
                Reset
              </button>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
              <MultiSelectDropdown
                label="District"
                options={districtOptions}
                valueField="value"
                displayField="label"
                selectedValue={selectedDistricts}
                setSelectedValue={setSelectedDistricts}
              />
              <MultiSelectDropdown
                label="Institute"
                options={instituteOptions}
                valueField="value"
                displayField="label"
                selectedValue={selectedInstitutes}
                setSelectedValue={setSelectedInstitutes}
              />
              <MultiSelectDropdown
                label="Class"
                options={classOptions}
                valueField="value"
                displayField="label"
                selectedValue={selectedClasses}
                setSelectedValue={setSelectedClasses}
              />
              <MultiSelectDropdown
                label="Gender"
                options={GENDER_OPTIONS}
                valueField="value"
                displayField="label"
                selectedValue={selectedGenders}
                setSelectedValue={setSelectedGenders}
              />
            </div>
          </div>
        )}

        {/* District + Institute Student Count (kept, cleaner UI) */}
        {!!summaryRows.length && (
          <div className="mx-auto mt-8">
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">
                    Student Count Summary
                  </h3>
                  <p className="mt-1 text-sm text-slate-600">
                    District-wise and institute-wise counts for the current
                    filters.
                  </p>
                </div>

                <div className="flex flex-wrap gap-2">
                  <div className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700">
                    Total Users:{" "}
                    <span className="font-semibold text-slate-900">
                      {filteredSummaryRows.length}
                    </span>
                  </div>
                  <div className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700">
                    Districts:{" "}
                    <span className="font-semibold text-slate-900">
                      {districtEntries.length}
                    </span>
                  </div>
                  <div className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700">
                    Institutes:{" "}
                    <span className="font-semibold text-slate-900">
                      {instituteEntries.length}
                    </span>
                  </div>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
                {/* District Count */}
                <div className="overflow-hidden rounded-xl border border-slate-200">
                  <div className="flex items-center justify-between bg-slate-50 px-4 py-3">
                    <h4 className="text-sm font-semibold text-slate-900">
                      District Student Count
                    </h4>
                    <span className="text-xs text-slate-500">
                      {districtEntries.length} districts
                    </span>
                  </div>

                  <div className="overflow-auto" style={{ height: "420px" }}>
                    <table className="min-w-full text-sm">
                      <thead className="sticky top-0 bg-white text-xs uppercase tracking-wide text-slate-500">
                        <tr className="border-b">
                          <th className="px-4 py-3 text-left">District</th>
                          <th className="px-4 py-3 text-right">Count</th>
                        </tr>
                      </thead>

                      <tbody className="divide-y divide-slate-100">
                        {districtEntries.map(([district, count], idx) => (
                          <tr
                            key={district}
                            className={
                              idx % 2 === 0 ? "bg-white" : "bg-slate-50/50"
                            }
                          >
                            <td className="px-4 py-3 font-medium text-slate-800">
                              {district}
                            </td>
                            <td className="px-4 py-3 text-right font-semibold text-slate-900">
                              {count}
                            </td>
                          </tr>
                        ))}

                        {!districtEntries.length && (
                          <tr>
                            <td
                              className="px-4 py-4 text-center text-slate-600"
                              colSpan={2}
                            >
                              No data for selected filters.
                            </td>
                          </tr>
                        )}
                      </tbody>

                      {!!districtEntries.length && (
                        <tfoot className="sticky bottom-0 bg-white">
                          <tr className="border-t">
                            <td className="px-4 py-3 font-semibold text-slate-900">
                              Total
                            </td>
                            <td className="px-4 py-3 text-right font-bold text-slate-900">
                              {districtEntries.reduce(
                                (total, [, count]) => total + (count || 0),
                                0,
                              )}
                            </td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                </div>

                {/* Institute Count */}
                <div className="overflow-hidden rounded-xl border border-slate-200">
                  <div className="flex items-center justify-between bg-slate-50 px-4 py-3">
                    <h4 className="text-sm font-semibold text-slate-900">
                      Institute Student Count
                    </h4>
                    <span className="text-xs text-slate-500">
                      {instituteEntries.length} institutes
                    </span>
                  </div>

                  <div className="overflow-auto" style={{ height: "420px" }}>
                    <table className="min-w-full text-sm">
                      <thead className="sticky top-0 bg-white text-xs uppercase tracking-wide text-slate-500">
                        <tr className="border-b">
                          <th className="px-4 py-3 text-left">Institute</th>
                          <th className="px-4 py-3 text-right">Count</th>
                        </tr>
                      </thead>

                      <tbody className="divide-y divide-slate-100">
                        {instituteEntries.map(([inst, count], idx) => (
                          <tr
                            key={inst}
                            className={
                              idx % 2 === 0 ? "bg-white" : "bg-slate-50/50"
                            }
                          >
                            <td className="px-4 py-3 font-medium text-slate-800">
                              {inst}
                            </td>
                            <td className="px-4 py-3 text-right font-semibold text-slate-900">
                              {count}
                            </td>
                          </tr>
                        ))}

                        {!instituteEntries.length && (
                          <tr>
                            <td
                              className="px-4 py-4 text-center text-slate-600"
                              colSpan={2}
                            >
                              No data for selected filters.
                            </td>
                          </tr>
                        )}
                      </tbody>

                      {!!instituteEntries.length && (
                        <tfoot className="sticky bottom-0 bg-white">
                          <tr className="border-t">
                            <td className="px-4 py-3 font-semibold text-slate-900">
                              Total
                            </td>
                            <td className="px-4 py-3 text-right font-bold text-slate-900">
                              {instituteEntries.reduce(
                                (total, [, count]) => total + (count || 0),
                                0,
                              )}
                            </td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {!!summaryRows.length && (
          <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-emerald-700/80">
                    Reference
                  </div>
                  <h4 className="text-sm font-semibold text-slate-900">
                    Phase → Districts
                  </h4>
                </div>
                <span className="text-xs text-slate-500">
                  {phaseDistrictEntries.length} phases
                </span>
              </div>

              <div
                className="mt-3 space-y-2 overflow-auto"
                style={{ maxHeight: "320px" }}
              >
                {phaseDistrictEntries.map(({ phase, districts }) => {
                  const isOpen = openPhasePanels.includes(phase);
                  return (
                    <div
                      key={phase}
                      className="overflow-hidden rounded-lg border border-slate-200"
                    >
                      <button
                        type="button"
                        className="flex w-full items-center justify-between bg-slate-50 px-4 py-2 text-left"
                        onClick={() =>
                          setOpenPhasePanels((prev) =>
                            prev.includes(phase)
                              ? prev.filter((p) => p !== phase)
                              : [...prev, phase],
                          )
                        }
                      >
                        <div className="text-sm font-semibold text-slate-900">
                          {phase}
                        </div>
                        <div className="text-xs text-slate-500">
                          {districts.length} districts {isOpen ? "▾" : "▸"}
                        </div>
                      </button>
                      {isOpen && (
                        <div className="bg-white max-h-[180px] overflow-auto">
                          <ul className="divide-y divide-slate-100 text-sm">
                            {districts.map((district) => (
                              <li
                                key={district}
                                className="px-4 py-2 text-slate-700"
                              >
                                {district}
                              </li>
                            ))}
                            {!districts.length && (
                              <li className="px-4 py-3 text-slate-500">
                                No districts for this phase.
                              </li>
                            )}
                          </ul>
                        </div>
                      )}
                    </div>
                  );
                })}

                {!phaseDistrictEntries.length && (
                  <div className="rounded-lg border border-dashed border-slate-200 p-4 text-center text-sm text-slate-600">
                    No phase data for selected filters.
                  </div>
                )}
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-emerald-700/80">
                    Reference
                  </div>
                  <h4 className="text-sm font-semibold text-slate-900">
                    Phase → Institutes
                  </h4>
                </div>
                <span className="text-xs text-slate-500">
                  {phaseInstituteEntries.length} phases
                </span>
              </div>

              <div
                className="mt-3 space-y-2 overflow-auto"
                style={{ maxHeight: "320px" }}
              >
                {phaseInstituteEntries.map(({ phase, institutes }) => {
                  const isOpen = openPhasePanels.includes(phase);
                  return (
                    <div
                      key={phase}
                      className="overflow-hidden rounded-lg border border-slate-200"
                    >
                      <button
                        type="button"
                        className="flex w-full items-center justify-between bg-slate-50 px-4 py-2 text-left"
                        onClick={() =>
                          setOpenPhasePanels((prev) =>
                            prev.includes(phase)
                              ? prev.filter((p) => p !== phase)
                              : [...prev, phase],
                          )
                        }
                      >
                        <div className="text-sm font-semibold text-slate-900">
                          {phase}
                        </div>
                        <div className="text-xs text-slate-500">
                          {institutes.length} institutes {isOpen ? "▾" : "▸"}
                        </div>
                      </button>
                      {isOpen && (
                        <div className="bg-white max-h-[180px] overflow-auto">
                          <ul className="divide-y divide-slate-100 text-sm">
                            {institutes.map((inst) => (
                              <li
                                key={inst}
                                className="px-4 py-2 text-slate-700"
                              >
                                {inst}
                              </li>
                            ))}
                            {!institutes.length && (
                              <li className="px-4 py-3 text-slate-500">
                                No institutes for this phase.
                              </li>
                            )}
                          </ul>
                        </div>
                      )}
                    </div>
                  );
                })}

                {!phaseInstituteEntries.length && (
                  <div className="rounded-lg border border-dashed border-slate-200 p-4 text-center text-sm text-slate-600">
                    No phase data for selected filters.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Group List (Users by District/Institute) */}
        <div className="mt-8 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <h4 className="text-base font-semibold text-slate-900">
              {groupHeading}
            </h4>
            <div className="text-sm text-slate-500">
              Groups:{" "}
              <span className="font-semibold text-slate-700">
                {groupEntries.length}
              </span>
            </div>
          </div>

          <div className="mt-4 space-y-3">
            {groupEntries.map(([groupName, count]) => {
              const isOpen = expandedGroup === groupName;

              return (
                <div
                  key={groupName}
                  className="rounded-xl border border-slate-200"
                >
                  <button
                    onClick={() => onToggleGroup(groupName)}
                    className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
                  >
                    <div>
                      <div className="text-sm font-semibold text-slate-900">
                        {groupName}
                      </div>
                      <div className="text-xs text-slate-500">
                        {count} users
                      </div>
                    </div>
                    <div className="text-slate-500">{isOpen ? "▾" : "▸"}</div>
                  </button>

                  {isOpen && (
                    <div className="border-t border-slate-200 p-4">
                      {/* Career Group Summary */}
                      <div className="p-2">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-sm font-semibold text-slate-900">
                              Career group interpretation summary
                            </div>
                            <div className="mt-1 text-xs text-slate-500">
                              Based on selected filters & latest attempt.
                            </div>
                          </div>
                          {careerGroupLoading && (
                            <div className="text-xs font-semibold text-slate-600">
                              Calculating…
                            </div>
                          )}
                        </div>

                        {!careerGroupLoading && !hasCareerGroupUsers && (
                          <div className="mt-3 text-sm text-slate-600">
                            No career group data available for current filters.
                          </div>
                        )}

                        {!careerGroupLoading && hasCareerGroupUsers && (
                          <div className="mt-3 overflow-x-auto rounded-lg border border-slate-200 bg-white">
                            <table className="min-w-full text-sm">
                              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600">
                                <tr>
                                  <th className="px-3 py-2 text-left">
                                    Career group
                                  </th>
                                  {INTERPRETATION_LABELS.map((label) => (
                                    <th
                                      key={label}
                                      className="px-3 py-2 text-left"
                                    >
                                      {label}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100">
                                {careerGroupSummary.map((entry) => (
                                  <tr
                                    key={entry.key}
                                    className="hover:bg-slate-50"
                                  >
                                    <td className="px-3 py-2 font-semibold text-slate-800">
                                      {entry.label}
                                    </td>
                                    {INTERPRETATION_LABELS.map((label) => (
                                      <td
                                        key={`${entry.key}-${label}`}
                                        className="px-3 py-2 text-slate-700"
                                      >
                                        {entry.counts[label]}
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {!groupEntries.length && (
              <div className="rounded-lg border border-dashed border-slate-200 p-6 text-center text-sm text-slate-600">
                No data to display for the selected filters.
              </div>
            )}
          </div>
        </div>

        {/* Career Group Meanings Drawer */}
        {showCareerGroupInfo && (
          <div className="fixed inset-0 z-50">
            <div
              className="absolute inset-0 bg-black/40"
              onClick={() => setShowCareerGroupInfo(false)}
            />
            <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-xl">
              <div className="flex items-center justify-between border-b px-4 py-3">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-amber-700/80">
                    Reference
                  </div>
                  <div className="text-base font-semibold text-slate-900">
                    Career Group Meanings
                  </div>
                </div>
                <button
                  className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  onClick={() => setShowCareerGroupInfo(false)}
                >
                  Close
                </button>
              </div>

              <div className="p-4">
                <div className="overflow-hidden rounded-xl border border-slate-200">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600">
                      <tr>
                        <th className="px-3 py-2 text-left">Career group</th>
                        <th className="px-3 py-2 text-left">Sectors</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {careerOptions.map((option, idx) => (
                        <tr
                          key={option.career_group}
                          className={idx % 2 ? "bg-white" : "bg-slate-50/40"}
                        >
                          <td className="px-3 py-2 font-semibold text-slate-800">
                            {option.career_group}
                          </td>
                          <td className="px-3 py-2 text-slate-700">
                            {option.text}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="mt-4 text-xs text-slate-500">
                  Tip: You can keep this open while exploring groups.
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Container>
  );
}
